import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import './App.css';
import ImageGrid from './ImageGrid';
import ImagePopup from './ImagePopup';
import About from './About';
import Home from './Home';
import Contact from './Contact';
import CV from './CV';
import ExhibitionsAndNews from './ExhibitionsAndNews';
import { motion, AnimatePresence } from 'framer-motion';


function AppContent() {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Persistent state for each grid
  const [galleryLoaded, setGalleryLoaded] = useState([]);
  const galleryCache = useRef(new Set());
  const [privateLoaded, setPrivateLoaded] = useState([]);
  const privateCache = useRef(new Set());

  useEffect(() => {
    const importAll = (r) => r.keys().map(r);
    const imageModules = importAll(require.context('./images', false, /\.(png|jpe?g|svg|JPG)$/));

    const imagesData = imageModules.map((imageModule) => {
      const pathParts = imageModule.split('/');
      const fileName = pathParts[pathParts.length - 1];
      const [title, year, type, dimensions, status] = fileName.replace(/\.[^/.]+$/, "").split(',');
      const cleanedDimensions = dimensions.split(')')[0] + ')';
      const dimensionMatch = cleanedDimensions.match(/(\d+(\.\d+)?) x (\d+(\.\d+)?)/);
      const width = parseFloat(dimensionMatch[3]);
      const height = parseFloat(dimensionMatch[1]);
      return {
        src: imageModule,
        title: title.replace('\'', '').replace('\'', '').trim(),
        year: parseInt(year.trim(), 10),
        type: type.trim(),
        dimensions: cleanedDimensions.trim(),
        width: width,
        height: height,
        status: status ? status.trim() : null
      };
    });

    setImages(imagesData);
  }, []);

  const handleImageClick = (image) => setSelectedImage(image);
  const closePopup = () => setSelectedImage(null);
  const timberlakeSunset = images.find(image => image.title === 'Timberlake Sunset');

  // Use location to control which grid is visible
  const location = useLocation();
  const isGallery = location.pathname === "/gallery";
  const isPrivate = location.pathname === "/private-collection";

  // Animation variants for the menu container
  const menuVariants = {
    closed: {
      opacity: 0,
      y: -10,
      scale: 0.98,
      pointerEvents: "none",
      transition: { duration: 0.25, when: "afterChildren" }
    },
    open: {
      opacity: 1,
      y: 0,
      scale: 1,
      pointerEvents: "auto",
      transition: { duration: 0.3, when: "beforeChildren", staggerChildren: 0.05 }
    }
  };

  // Animation variants for each link
  const linkVariants = {
    closed: { opacity: 0, x: 20 },
    open: { opacity: 1, x: 0 }
  };


  return (
    <div className="App" onContextMenu={(e) => e.preventDefault()}>
      <div className="top-bar">
        <div className="logo">Seiji Yonehara</div>
        <nav className="nav-menu">
          <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)}>
            &#9776;
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="menu-links"
                initial="closed"
                animate="open"
                exit="closed"
                variants={menuVariants}
              >
                {[
                  { to: "/", label: "Home" },
                  { to: "/gallery", label: "Gallery" },
                  { to: "/private-collection", label: "Private Collection" },
                  { to: "/about", label: "About" },
                  { to: "/contact", label: "Contact" },
                  { to: "/cv", label: "C.V." },
                  { to: "/exhibitions-and-news", label: "Exhibitions And News" }
                ].map(({ to, label }, idx, arr) => (
                  <React.Fragment key={to}>
                    <motion.div
                      variants={linkVariants}
                      style={{ width: "100%" }}
                    >
                      <Link to={to} onClick={() => setMenuOpen(false)}>
                        {label}
                      </Link>
                    </motion.div>
                    {idx < arr.length - 1 && (
                      <div
                        style={{
                          width: "95%",
                          height: "1px",
                          background: "#555",
                          margin: "2px 0 2px 0",
                          alignSelf: "stretch"
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </div>
      <Routes>
        <Route path="/" element={timberlakeSunset && <Home image={timberlakeSunset} />} />
        <Route path="/cv" element={<CV />} />
        <Route path="/exhibitions-and-news" element={<ExhibitionsAndNews />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
      {/* Always mounted grids, only one visible at a time */}
      <div style={{ display: isGallery ? "block" : "none" }}>
        <ImageGrid
          images={images.filter((image) => image.status === null)}
          onImageClick={handleImageClick}
          loadedImages={galleryLoaded}
          setLoadedImages={setGalleryLoaded}
          cacheRef={galleryCache}
        />
      </div>
      <div style={{ display: isPrivate ? "block" : "none" }}>
        <ImageGrid
          images={images.filter((image) => image.status !== null)}
          onImageClick={handleImageClick}
          loadedImages={privateLoaded}
          setLoadedImages={setPrivateLoaded}
          cacheRef={privateCache}
        />
      </div>
      {selectedImage && <ImagePopup image={selectedImage} onClose={closePopup} />}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}